import { useCredits, useNavigation } from "@utilities/hooks";
import { CreditDataType } from "@utilities/types";
import { useCallback, useEffect, useMemo, useState } from "react";

export const useController = () => {

    const { goBack } = useNavigation();
    const {
        credits, closedCredits, onCreditPayment,
        onGetAllOrganisationCredits, onGetClosedCredits,
        creditPaymentLoading, creditPaymentError, creditPaymentSuccess,
        resetCreditPaymentStatus,
    } = useCredits();

    const [searchQuery, setSearchQuery] = useState<string>("");
    const [activeTab, setActiveTab] = useState<number>(0);

    const tabs = useMemo(() => [
        { index: 0, label: "Crédits en cours" },
        { index: 1, label: "Historique (soldés)" },
    ], []);

    const filteredCredits = useMemo(() => {
        if (!credits) return [];

        const lowerCaseQuery = searchQuery.toLowerCase();

        return credits.filter((credit) => {
            const { _clientID: client, _saleID: order } = credit;
            const clientFirstname = client?.firstname?.toLowerCase() || "";
            const clientLastname = client?.lastname?.toLowerCase() || "";
            const orderNumber = order?.orderNumber?.toLowerCase() || "";

            return (
                clientFirstname.includes(lowerCaseQuery) ||
                clientLastname.includes(lowerCaseQuery) ||
                orderNumber.includes(lowerCaseQuery)
            );
        });
    }, [credits, searchQuery]);

    const filteredClosedCredits = useMemo(() => {
        if (!closedCredits) return [];

        const lowerCaseQuery = searchQuery.toLowerCase();

        return closedCredits.filter((credit) => {
            const { _clientID: client, _saleID: order } = credit;
            const clientFirstname = client?.firstname?.toLowerCase() || "";
            const clientLastname = client?.lastname?.toLowerCase() || "";
            const orderNumber = order?.orderNumber?.toLowerCase() || "";

            return (
                clientFirstname.includes(lowerCaseQuery) ||
                clientLastname.includes(lowerCaseQuery) ||
                orderNumber.includes(lowerCaseQuery)
            );
        });
    }, [closedCredits, searchQuery]);

    const totalRemainingAmount = useMemo(() => {
        return filteredCredits.reduce((total, credit) => total + credit.remainingToBePaid, 0);
    }, [filteredCredits]);

    const totalClosedAmount = useMemo(() => {
        return filteredClosedCredits.reduce((total, credit) => total + credit.amount, 0);
    }, [filteredClosedCredits]);

    useEffect(() => {
        onGetAllOrganisationCredits();
        onGetClosedCredits();
    }, []);

    const handleCreditPayment = useCallback(async (creditID: string, creditData: CreditDataType) => {
        await onCreditPayment(creditID, creditData);
    }, [onCreditPayment]);

    const handleTabSwitch = useCallback((tabIndex: number) => {
        setActiveTab(tabIndex);
    }, []);

    return {
        filteredCredits, filteredClosedCredits, goBack,
        totalRemainingAmount, totalClosedAmount,
        handleCreditPayment, setSearchQuery,
        creditPaymentLoading, creditPaymentError, creditPaymentSuccess,
        resetCreditPaymentStatus,
        tabs, activeTab, handleTabSwitch,
    }
}
